/**
 * Markdown → HTML, for previewing generated content.
 *
 * Hand-written rather than pulling in marked/react-markdown: the client's
 * dependency list is deliberately four packages, and what a model emits for a
 * `markdown` category is ordinary prose — headings, lists, tables, emphasis,
 * code. This covers that, and anything it does not recognise falls through as
 * text rather than disappearing.
 *
 * NOTHING here sanitises. Markdown permits raw HTML inline, and by the time
 * this returns, model-authored `<script>` is still model-authored `<script>`.
 * The output is only ever handed to HtmlPreview's `sandbox=""` iframe, which
 * is what actually contains it — same containment the html response type
 * relies on. Do not render this string with dangerouslySetInnerHTML.
 */

/** Escapes text destined for a context where markup must not be interpreted. */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Inline spans, innermost-first so a link's label can still carry emphasis.
 *
 * Code spans are extracted before anything else and restored at the end:
 * `**not bold**` inside backticks is literal, and running the emphasis passes
 * over it would rewrite content the author marked as verbatim.
 */
function renderInline(text: string): string {
  const codeSpans: string[] = [];
  let out = text.replace(/`([^`]+)`/g, (_match, code: string) => {
    codeSpans.push(`<code>${escapeHtml(code)}</code>`);
    return ` CODE${codeSpans.length - 1} `;
  });

  out = escapeHtml(out);

  // Images before links — the syntaxes differ only by a leading '!', so the
  // link pattern would otherwise claim both and drop the image.
  out = out.replace(
    /!\[([^\]]*)\]\(([^)\s]+)\)/g,
    (_m, alt: string, src: string) => `<img src="${src}" alt="${alt}">`,
  );
  out = out.replace(
    /\[([^\]]+)\]\(([^)\s]+)\)/g,
    (_m, label: string, href: string) => `<a href="${href}">${label}</a>`,
  );

  out = out.replace(/\*\*\*([^*]+)\*\*\*/g, "<strong><em>$1</em></strong>");
  out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  out = out.replace(/(^|[^*])\*([^*]+)\*/g, "$1<em>$2</em>");
  out = out.replace(/__([^_]+)__/g, "<strong>$1</strong>");
  out = out.replace(/~~([^~]+)~~/g, "<del>$1</del>");

  return out.replace(/ CODE(\d+) /g, (_m, i: string) => codeSpans[Number(i)] ?? "");
}

/** A table needs its delimiter row (`| --- |`) to be a table at all. */
function isDelimiterRow(line: string): boolean {
  return /^\s*\|?[\s:|-]+\|[\s:|-]*$/.test(line) && line.includes("-");
}

function splitRow(line: string): string[] {
  return line
    .replace(/^\s*\|/, "")
    .replace(/\|\s*$/, "")
    .split("|")
    .map((cell) => cell.trim());
}

export function renderMarkdown(source: string): string {
  const lines = source.replace(/\r\n/g, "\n").split("\n");
  const html: string[] = [];

  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;

    // Fenced code. Consumed verbatim — markdown inside a fence is not markup,
    // and the closing fence may legitimately never arrive on truncated output.
    const fence = /^\s*```(\w*)\s*$/.exec(line);
    if (fence) {
      const lang = fence[1] ?? "";
      const body: string[] = [];
      i += 1;
      while (i < lines.length && !/^\s*```\s*$/.test(lines[i]!)) {
        body.push(lines[i]!);
        i += 1;
      }
      i += 1; // the closing fence, or the end of input
      const cls = lang ? ` class="language-${escapeHtml(lang)}"` : "";
      html.push(`<pre><code${cls}>${escapeHtml(body.join("\n"))}</code></pre>`);
      continue;
    }

    if (!line.trim()) {
      i += 1;
      continue;
    }

    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      const level = heading[1]!.length;
      html.push(`<h${level}>${renderInline(heading[2]!.trim())}</h${level}>`);
      i += 1;
      continue;
    }

    if (/^\s*(?:---+|\*\*\*+|___+)\s*$/.test(line)) {
      html.push("<hr>");
      i += 1;
      continue;
    }

    // Table: a header row followed by a delimiter row.
    if (line.includes("|") && i + 1 < lines.length && isDelimiterRow(lines[i + 1]!)) {
      const headers = splitRow(line);
      i += 2;
      const bodyRows: string[][] = [];
      while (i < lines.length && lines[i]!.includes("|") && lines[i]!.trim()) {
        bodyRows.push(splitRow(lines[i]!));
        i += 1;
      }
      const head = headers.map((h) => `<th>${renderInline(h)}</th>`).join("");
      const body = bodyRows
        .map((row) => `<tr>${row.map((c) => `<td>${renderInline(c)}</td>`).join("")}</tr>`)
        .join("");
      html.push(`<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`);
      continue;
    }

    if (/^\s*>\s?/.test(line)) {
      const quoted: string[] = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i]!)) {
        quoted.push(lines[i]!.replace(/^\s*>\s?/, ""));
        i += 1;
      }
      html.push(`<blockquote>${renderMarkdown(quoted.join("\n"))}</blockquote>`);
      continue;
    }

    const bulletStart = /^\s*[-*+]\s+/.test(line);
    const orderedStart = /^\s*\d+[.)]\s+/.test(line);
    if (bulletStart || orderedStart) {
      const tag = bulletStart ? "ul" : "ol";
      const pattern = bulletStart ? /^\s*[-*+]\s+/ : /^\s*\d+[.)]\s+/;
      const items: string[] = [];
      while (i < lines.length && pattern.test(lines[i]!)) {
        let item = lines[i]!.replace(pattern, "");
        i += 1;
        // Continuation lines: indented, and not the start of the next item.
        while (i < lines.length && /^\s{2,}\S/.test(lines[i]!) && !pattern.test(lines[i]!)) {
          item += ` ${lines[i]!.trim()}`;
          i += 1;
        }
        items.push(`<li>${renderInline(item)}</li>`);
      }
      html.push(`<${tag}>${items.join("")}</${tag}>`);
      continue;
    }

    // Paragraph: everything up to a blank line or the start of another block.
    const para: string[] = [];
    while (
      i < lines.length &&
      lines[i]!.trim() &&
      !/^\s*(?:#{1,6}\s|>|```|[-*+]\s|\d+[.)]\s)/.test(lines[i]!) &&
      !/^\s*(?:---+|\*\*\*+|___+)\s*$/.test(lines[i]!)
    ) {
      para.push(lines[i]!);
      i += 1;
    }
    if (para.length > 0) {
      html.push(`<p>${renderInline(para.join("\n")).replace(/\n/g, "<br>")}</p>`);
    }
  }

  return html.join("\n");
}

/**
 * Base styling for rendered markdown, prepended to the HTML before it goes to
 * the iframe.
 *
 * Markdown carries no CSS of its own, so without this it lands on browser
 * defaults — most visibly, tables render with no borders at all. This is kept
 * out of HtmlPreview's own base stylesheet on purpose: for the html response
 * type the category's CSS is the design, and adding table/heading rules there
 * would start overriding content that styled itself.
 */
export function markdownDocument(source: string): string {
  return `<style>
  body { max-width: 820px; line-height: 1.6; }
  h1, h2, h3, h4, h5, h6 { line-height: 1.25; margin: 1.4em 0 0.5em; }
  h1 { font-size: 1.8rem; } h2 { font-size: 1.45rem; } h3 { font-size: 1.2rem; }
  p, ul, ol, blockquote, table, pre { margin: 0 0 1em; }
  ul, ol { padding-left: 1.4em; }
  li { margin: 0.25em 0; }
  a { color: #2f6df6; }
  code { background: #f0f2f6; padding: 0.15em 0.35em; border-radius: 4px;
         font-size: 0.9em; font-family: ui-monospace, monospace; }
  pre { background: #f5f7fa; padding: 12px 14px; border-radius: 8px;
        overflow-x: auto; }
  pre code { background: none; padding: 0; }
  blockquote { margin-left: 0; padding-left: 1em; color: #5a6478;
               border-left: 3px solid #d7dce5; }
  table { border-collapse: collapse; width: 100%; }
  th, td { border: 1px solid #d7dce5; padding: 7px 10px; text-align: left; }
  th { background: #f5f7fa; }
  img { max-width: 100%; }
  hr { border: none; border-top: 1px solid #d7dce5; margin: 1.6em 0; }
</style>
${renderMarkdown(source)}`;
}

/**
 * Detects whether raw string content is structured as Markdown rather than HTML.
 * Used by ContentPreview so multi-step pipeline outputs (which frequently emit
 * markdown drafts even when the pipeline response_type defaults to "html")
 * render properly instead of collapsing as unparsed plain text.
 */
export function isLikelyMarkdown(text: string): boolean {
  if (!text || typeof text !== "string") return false;
  const trimmed = text.trim();
  if (!trimmed) return false;

  // Complete HTML documents or SVG/XML documents are never pure markdown.
  if (/^<!doctype/i.test(trimmed) || /^<html/i.test(trimmed) || /^<svg/i.test(trimmed)) return false;

  // Check for distinct markdown markers
  const hasHeading = /(?:^|\n)\s*#{1,6}\s+\S/.test(trimmed);
  const hasBulletList = /(?:^|\n)\s*[-*+]\s+\S/.test(trimmed);
  const hasNumberedList = /(?:^|\n)\s*\d+\.\s+\S/.test(trimmed);
  const hasBold = /\*\*[^*]+\*\*/.test(trimmed) || /__[^_]+__/.test(trimmed);
  const hasCodeFence = /```[\s\S]*?```/.test(trimmed);
  const hasBlockquote = /(?:^|\n)\s*>\s+\S/.test(trimmed);
  const hasTable = /(?:^|\n)\|?.+\|.+\|(?:^|\n)\|?\s*[-:]+[-| :]*\|/.test(trimmed);

  // Count standard HTML container/block tags
  const htmlTagMatches = trimmed.match(/<\/?(?:div|p|h[1-6]|ul|ol|li|table|tr|td|th|section|article|header|footer|main|aside|nav|body|head)\b/gi);
  const htmlTagCount = htmlTagMatches ? htmlTagMatches.length : 0;

  let mdScore = 0;
  if (hasHeading) mdScore += 3;
  if (hasCodeFence) mdScore += 3;
  if (hasTable) mdScore += 3;
  if (hasBulletList) mdScore += 2;
  if (hasNumberedList) mdScore += 2;
  if (hasBold) mdScore += 1;
  if (hasBlockquote) mdScore += 1;

  // If there are significant markdown markers and it's not predominantly HTML
  if (mdScore >= 3 && htmlTagCount <= 2) return true;
  if (mdScore >= 2 && htmlTagCount === 0) return true;

  return false;
}
